import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { AffinityScript } from '../components/meal-config/affinityScript';

export type ConfigPhase = 'idle' | 'config' | 'thinking' | 'cascade' | 'done';
export type DietKind = 'auto' | 'fish' | 'veg' | 'meat';

interface CellOverride {
  portions: number;
  kind: DietKind;
}

interface ToastState {
  text: string;
  undoKey?: string;
}

// Dinner (souper) slot keys — mi=2, di=0..6 → slotKey = 2*7 + di = 14..20.
// Used as the default weekly selection when first entering config.
export const DEFAULT_SELECTION = [14, 15, 16, 17, 18, 19, 20];

interface MealPlanUIState {
  // Config phase
  configPhase: ConfigPhase;
  configSelections: number[];
  configPerCell: Record<number, CellOverride>;
  configWeekPortions: number;
  configTargets: { fish: number; veg: number; meat: number };
  configPlan: Record<number, string>;
  configShown: number[];
  /** What the « Les affinités » panel plays while thinking; null outside a generation. */
  configScript: AffinityScript | null;
  /**
   * The slots the running generation fills. Régénérer from the week view runs
   * with no config selection at all, so without this the grid could not tell
   * which cells to shimmer and then reveal — the cascade played on nothing.
   */
  configFilling: number[];
  /**
   * The day the running generation's first column falls on, or null when no
   * generation is running (the config screen then shows the Monday week). The
   * phone's Régénérer starts on today (#808), so the reveal has to label its
   * days from here rather than assume Monday.
   */
  configWindow: string | null;
  // Weekly view UI
  sel: number | null;
  moving: boolean;
  libFor: number | null;
  detailFor: string | null;
  flash: number | null;
  toast: ToastState | null;
  /**
   * Bumped by `plannerHome`, so the week view can close what it keeps in its
   * own state (the slot menu, the entry form) along with what lives here.
   */
  homeRev: number;
}

const initialState: MealPlanUIState = {
  configPhase: 'idle',
  configSelections: [],
  configPerCell: {},
  configWeekPortions: 4,
  configTargets: { fish: 2, veg: 2, meat: 1 },
  configPlan: {},
  configShown: [],
  configScript: null,
  configFilling: [],
  configWindow: null,
  sel: null,
  moving: false,
  libFor: null,
  detailFor: null,
  flash: null,
  toast: null,
  homeRev: 0,
};

function cancelConfigState(state: MealPlanUIState) {
  state.configPhase = 'idle';
  state.configPlan = {};
  state.configShown = [];
  state.configScript = null;
  state.configFilling = [];
  state.configWindow = null;
}

const mealPlanUISlice = createSlice({
  name: 'mealPlanUI',
  initialState,
  reducers: {
    setConfigPhase(state, action: PayloadAction<ConfigPhase>) {
      state.configPhase = action.payload;
      if (action.payload === 'idle' || action.payload === 'config') {
        state.configScript = null;
        state.configFilling = [];
        state.configWindow = null;
      }
      if (action.payload === 'config') {
        // Reset cascade state when entering config
        state.configShown = [];
        state.configPlan = {};
        // Default the weekly selection to all soupers on a fresh entry
        // (don't clobber an existing selection when re-opening config).
        if (state.configSelections.length === 0) {
          state.configSelections = [...DEFAULT_SELECTION];
        }
      }
    },
    toggleSlot(state, action: PayloadAction<number>) {
      const idx = state.configSelections.indexOf(action.payload);
      if (idx === -1) {
        state.configSelections.push(action.payload);
      } else {
        state.configSelections.splice(idx, 1);
        delete state.configPerCell[action.payload];
      }
    },
    setSelections(state, action: PayloadAction<number[]>) {
      state.configSelections = action.payload;
    },
    setCellOverride(state, action: PayloadAction<{ slotKey: number; override: CellOverride }>) {
      state.configPerCell[action.payload.slotKey] = action.payload.override;
    },
    removeCellOverride(state, action: PayloadAction<number>) {
      delete state.configPerCell[action.payload];
    },
    /** Week navigation in config resets per-cell overrides (spec: changing week resets selection). */
    clearCellOverrides(state) {
      state.configPerCell = {};
    },
    setWeekPortions(state, action: PayloadAction<number>) {
      state.configWeekPortions = Math.max(1, Math.min(12, action.payload));
    },
    setConfigTargets(state, action: PayloadAction<{ fish: number; veg: number; meat: number }>) {
      state.configTargets = action.payload;
    },
    setConfigPlan(state, action: PayloadAction<Record<number, string>>) {
      state.configPlan = action.payload;
    },
    setConfigScript(state, action: PayloadAction<AffinityScript | null>) {
      state.configScript = action.payload;
    },
    setConfigFilling(state, action: PayloadAction<number[]>) {
      state.configFilling = action.payload;
    },
    setConfigWindow(state, action: PayloadAction<string | null>) {
      state.configWindow = action.payload;
    },
    addShown(state, action: PayloadAction<number>) {
      if (!state.configShown.includes(action.payload)) {
        state.configShown.push(action.payload);
      }
    },
    setSel(state, action: PayloadAction<number | null>) {
      state.sel = action.payload;
      state.moving = false;
    },
    setMoving(state, action: PayloadAction<boolean>) {
      state.moving = action.payload;
    },
    setLibFor(state, action: PayloadAction<number | null>) {
      state.libFor = action.payload;
    },
    setDetailFor(state, action: PayloadAction<string | null>) {
      state.detailFor = action.payload;
    },
    setFlash(state, action: PayloadAction<number | null>) {
      state.flash = action.payload;
    },
    setToast(state, action: PayloadAction<ToastState>) {
      state.toast = action.payload;
    },
    clearToast(state) {
      state.toast = null;
    },
    resetConfig(state) {
      state.configPhase = 'idle';
      state.configSelections = [];
      state.configPerCell = {};
      state.configPlan = {};
      state.configShown = [];
      state.configScript = null;
      state.configFilling = [];
      state.configWindow = null;
    },
    /**
     * Back out of the planner without generating: returns to the week view and
     * drops the half-revealed cascade. Unlike resetConfig it KEEPS the selection,
     * the per-cell overrides and the targets — cancelling means "not now", not
     * "throw away what I just set up", so re-opening resumes where you left off.
     */
    cancelConfig(state) {
      cancelConfigState(state);
    },
    /**
     * « Mes repas » pressed while already on the planner: back to the plain
     * week. It closes the drawers and the settings screen — settings the way
     * Cancel does, so what was set up is kept for next time. A generation that
     * is running is left alone: it is already writing the week. The week itself
     * goes back to this one through `setCurrentWeek`, which lives in mealPlan.
     *
     * The sidebar entry used to do nothing on its own route, so a cook who had
     * drifted to another week with a drawer open had no way back (8 Oct 2026).
     */
    plannerHome(state) {
      state.sel = null;
      state.libFor = null;
      state.detailFor = null;
      state.moving = false;
      if (state.configPhase === 'config') cancelConfigState(state);
      state.homeRev += 1;
    },
  },
});

export const {
  setConfigPhase,
  toggleSlot,
  setSelections,
  setCellOverride,
  removeCellOverride,
  clearCellOverrides,
  setWeekPortions,
  setConfigTargets,
  setConfigPlan,
  setConfigScript,
  setConfigFilling,
  setConfigWindow,
  addShown,
  setSel,
  setMoving,
  setLibFor,
  setDetailFor,
  setFlash,
  setToast,
  clearToast,
  resetConfig,
  cancelConfig,
  plannerHome,
} = mealPlanUISlice.actions;

export default mealPlanUISlice.reducer;

// Selectors
export const selectConfigPhase = (state: { mealPlanUI: MealPlanUIState }) => state.mealPlanUI.configPhase;
export const selectConfigSelections = (state: { mealPlanUI: MealPlanUIState }) => state.mealPlanUI.configSelections;
export const selectConfigPerCell = (state: { mealPlanUI: MealPlanUIState }) => state.mealPlanUI.configPerCell;
export const selectConfigWeekPortions = (state: { mealPlanUI: MealPlanUIState }) => state.mealPlanUI.configWeekPortions;
export const selectConfigTargets = (state: { mealPlanUI: MealPlanUIState }) => state.mealPlanUI.configTargets;
export const selectConfigPlan = (state: { mealPlanUI: MealPlanUIState }) => state.mealPlanUI.configPlan;
export const selectConfigScript = (state: { mealPlanUI: MealPlanUIState }) => state.mealPlanUI.configScript;
export const selectConfigFilling = (state: { mealPlanUI: MealPlanUIState }) => state.mealPlanUI.configFilling;
export const selectConfigWindow = (state: { mealPlanUI: MealPlanUIState }) => state.mealPlanUI.configWindow;
export const selectConfigShown = (state: { mealPlanUI: MealPlanUIState }) => state.mealPlanUI.configShown;
export const selectSel = (state: { mealPlanUI: MealPlanUIState }) => state.mealPlanUI.sel;
export const selectMoving = (state: { mealPlanUI: MealPlanUIState }) => state.mealPlanUI.moving;
export const selectLibFor = (state: { mealPlanUI: MealPlanUIState }) => state.mealPlanUI.libFor;
export const selectDetailFor = (state: { mealPlanUI: MealPlanUIState }) => state.mealPlanUI.detailFor;
export const selectFlash = (state: { mealPlanUI: MealPlanUIState }) => state.mealPlanUI.flash;
export const selectToast = (state: { mealPlanUI: MealPlanUIState }) => state.mealPlanUI.toast;
export const selectHomeRev = (state: { mealPlanUI: MealPlanUIState }) => state.mealPlanUI.homeRev;
